import type { GeoHit } from '@rfleet/shared';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useMap } from './MapView';

/**
 * Address search box overlaid on a map (GET /geo/search). Picking a hit flies the map there;
 * `onPick` lets the page react (e.g. pre-fill a place's address). Must be rendered inside <MapView>.
 */
export function GeoSearch({ onPick, className }: { onPick?: (hit: GeoHit) => void; className?: string }) {
  const { t } = useTranslation();
  const map = useMap();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const res = useQuery({ queryKey: ['geo-search', debounced], queryFn: () => api<GeoHit[]>(`/geo/search?q=${encodeURIComponent(debounced)}&limit=6`), enabled: debounced.length >= 2, staleTime: 60_000 });
  const hits = res.data ?? [];

  const pick = (h: GeoHit) => {
    setOpen(false);
    setQ(h.label);
    map?.flyTo({ center: [h.lon, h.lat], zoom: Math.max(map.getZoom(), 15), essential: true });
    onPick?.(h);
  };

  return (
    <div className={className ?? 'absolute left-1/2 top-2.5 z-[3] w-[min(400px,calc(100%-112px))] -translate-x-1/2'} dir={document.documentElement.dir}>
      <label className="flex items-center gap-2 rounded-lg border border-border bg-surface/95 px-3 py-1.5 text-[13px] shadow-overlay backdrop-blur focus-within:border-brand-ink">
        <Search size={15} className="text-text-muted" aria-hidden />
        <input
          className="w-full bg-transparent outline-none placeholder:text-text-muted"
          placeholder={t('map.searchAddress')}
          aria-label={t('map.searchAddress')}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter') { e.preventDefault(); if (hits[active]) pick(hits[active]); }
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        {q ? <button type="button" className="text-text-muted hover:text-text" aria-label={t('common.cancel')} onClick={() => { setQ(''); setOpen(false); }}><X size={14} /></button> : null}
      </label>
      {open && debounced.length >= 2 ? (
        <ul className="mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-overlay" role="listbox">
          {res.isLoading ? <li className="px-3 py-2 text-xs text-text-muted">{t('common.loading')}</li> : !hits.length ? <li className="px-3 py-2 text-xs text-text-muted">{t('map.noResults')}</li> : hits.map((h, i) => (
            <li key={`${h.lat},${h.lon}`} role="option" aria-selected={i === active}>
              <button type="button" className={`flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm ${i === active ? 'bg-brand-soft' : 'hover:bg-surface-hover'}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(h)}>
                <MapPin size={15} className="flex-none text-text-muted" aria-hidden />
                <span className="line-clamp-2">{h.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
